import { useEffect, useRef, useState } from 'react';
import { listSaltUnitTypes, type SaltUnitType } from '../api/salt-unit-types-api';

// Short-lived in-memory cache so multiple consumers within a single
// render cycle hit the API at most once. The data changes rarely
// (admin-edited lookup table), so a 5-minute TTL is comfortable. The
// SALT admin page invalidates this on every successful mutation via
// invalidateSaltUnitTypesCache().

const TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  data: SaltUnitType[];
  fetchedAt: number;
}

let cache: { all: CacheEntry | null; active: CacheEntry | null } = { all: null, active: null };
const subscribers = new Set<() => void>();

function invalidate() {
  cache = { all: null, active: null };
  subscribers.forEach((fn) => fn());
}

export function invalidateSaltUnitTypesCache() {
  invalidate();
}

function getCached(activeOnly: boolean): SaltUnitType[] | null {
  const entry = activeOnly ? cache.active : cache.all;
  if (!entry) return null;
  if (Date.now() - entry.fetchedAt > TTL_MS) return null;
  return entry.data;
}

function setCached(activeOnly: boolean, data: SaltUnitType[]) {
  const entry = { data, fetchedAt: Date.now() };
  if (activeOnly) cache.active = entry;
  else cache.all = entry;
}

interface UseSaltUnitTypesResult {
  data: SaltUnitType[] | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSaltUnitTypes(activeOnly = false): UseSaltUnitTypesResult {
  const [data, setData] = useState<SaltUnitType[] | null>(() => getCached(activeOnly));
  const [loading, setLoading] = useState(data === null);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    function refresh() {
      const cached = getCached(activeOnly);
      if (cached) {
        setData(cached);
        setLoading(false);
        return;
      }
      setLoading(true);
      listSaltUnitTypes(activeOnly)
        .then((rows) => {
          setCached(activeOnly, rows);
          if (mountedRef.current) {
            setData(rows);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (mountedRef.current) setError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          if (mountedRef.current) setLoading(false);
        });
    }
    refresh();
    subscribers.add(refresh);
    return () => { subscribers.delete(refresh); };
  }, [activeOnly]);

  function refetch() {
    invalidate();
  }

  return { data, loading, error, refetch };
}
